import { Injectable } from '@angular/core';
import { FlexiLogic, FlexiExpression, FlexiLogicResult, FlexiExpressionData } from '@customer-apps/flexi-logic';
import { Session } from '@customer-apps/flexi-paths';
import { DataSourceItem, DataSourceType } from '../../v-api';
import { ContextModelState, DataSource, TemplateSlot } from '../interfaces';
import { setNestedValue } from '../utils';

/**
 * Local data model for JSON Logic
 */
@Injectable({
    providedIn: 'root'
})
export class ContextModelService {
    private _contextModelState: ContextModelState;
    private _contextModelCurrentState: ContextModelState;
    private _session: Session | undefined;

    get state(): ContextModelState {
        return this._contextModelState;
    }

    get currentState(): ContextModelState {
        return this._contextModelCurrentState;
    }

    get session(): Session | undefined {
        return this._session;
    }

    set session(value: Session | undefined) {
        this._session = value;
    }

    init(state: ContextModelState): void {
        const contextModelState = {
            variables: state?.variables || {},
            globals: state?.globals || {},
            dataSources: state?.dataSources || {},
            project: state.project,
            inputs: state?.inputs || {}
        };
        this.initState({
            ...contextModelState,
            project: JSON.parse(JSON.stringify(state.project))
        });
        this.initCurrentState({
            ...contextModelState,
            project: JSON.parse(JSON.stringify(state.project))
        });
    }

    initState(state: ContextModelState): void {
        this._contextModelState = state;
    }

    initCurrentState(state: ContextModelState): void {
        this._contextModelCurrentState = state;
    }

    setDataSource(dataSourceType: DataSourceType, dataSource: DataSource): void {
        this._contextModelCurrentState.dataSources ??= {};
        this._contextModelCurrentState.dataSources[dataSourceType] = dataSource;
    }

    getDataSource(dataSourceType: DataSourceType): DataSource {
        this._contextModelCurrentState.dataSources ??= {};
        return this._contextModelCurrentState.dataSources[dataSourceType];
    }

    processJsonLogic(
        logic: FlexiExpression | FlexiExpressionData,
        options?: { context?: Partial<ContextModelState> & { [key: string]: any } }
    ): FlexiLogicResult | null {
        const context = options?.context ? { ...this.currentState, ...options?.context } : this.currentState;
        const session = this._session;
        return FlexiLogic.calculate(logic, context, session);
    }

    selectDataSourceItem(slot: TemplateSlot, value: any | any[]): void {
        if (!slot.dataSourceMapping) {
            return;
        }
        const items: DataSourceItem[] = FlexiLogic.calculate(slot.dataSourceMapping, this.currentState) as DataSourceItem[];
        if (!slot.dataSourceSelectionOutput) {
            return;
        }
        if (Array.isArray(value)) {
            const valueItems = items?.filter(i => value.includes(i.value));
            setNestedValue(this._contextModelCurrentState, slot.dataSourceSelectionOutput, valueItems);
        } else {
            const valueItem = items?.find(i => i.value === value);
            setNestedValue(this._contextModelCurrentState, slot.dataSourceSelectionOutput, valueItem);
        }
    }
}
